import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import {
  getAllSettings,
  addSetting,
  removeSetting,
  UserSettingPair,
  getAdminPricing,
  setAdminPricing,
  resetAdminPricing,
  AdminPricing,
} from '../../../services/admin/superAdminSettingsService';
import { getAllAdminUsers, AdminUserWithStaff } from '../../../services/admin/adminService';
import { AdminLayout } from './AdminLayout';

// Existing labels remain literal; new entries can supply translation keys.
interface AccountFeatureSetting {
  key: string;
  label: string;
  description: string;
  translated?: boolean;
}

// All available settings with their display info
const SETTING_GROUPS: { name: string; settings: AccountFeatureSetting[] }[] = [
  {
    name: 'Page Access',
    settings: [
      { key: 'mep_page', label: 'MEP Page', description: 'MEP planning (admin + staff with mep_edit)' },
      { key: 'checklists_page', label: 'Checklists', description: 'Checklists (admin + staff with checklist_edit)' },
      { key: 'recipes_page', label: 'Recipes', description: 'Recipes (admin + staff with recipe_edit)' },
      { key: 'notifications_page', label: 'Notifications', description: 'Notifications (admin + staff with notifications_edit)' },
      { key: 'waste_page', label: 'Waste Tracking', description: 'Waste tracking (admin + staff with waste_edit)' },
      { key: 'invoices_page', label: 'Invoices', description: 'Access to invoices + order deletion (staff inherits)' },
      { key: 'inventory_sections_page', label: 'Inventory', description: 'Inventory sections + counts (admin + staff with inventory_edit)' },
      { key: 'reports_page', label: 'Reports', description: 'Reports page (admin + staff inherit)' },
      { key: 'cost_percent_page', label: 'Cost Percent (Inslag)', description: 'Cost percentage analytics page (admin-only, in testing)' },
      { key: 'menu_change_dashboard', label: 'Menu Change Dashboard', description: 'Menu change rollout tracking (admin + staff inherit)' },
      { key: 'analysis_page', label: 'Analysis', description: 'Analysis page: action items, waste + money savers overviews, on-demand reports (admin-only, in testing)' },
    ],
  },
  {
    name: 'Account Features',
    settings: [
      { key: 'customer_notifications', label: 'customerRequests.permission.label', description: 'customerRequests.permission.description', translated: true },
      { key: 'account_setup', label: 'accountSetup.permission.label', description: 'accountSetup.permission.description', translated: true },
      { key: 'ambassador', label: 'ambassador.permission.label', description: 'ambassador.permission.description', translated: true },
      { key: 'dashboard_food_only', label: 'Food-Only Dashboard', description: 'Dashboard shows food suppliers only (staff inherits)' },
      { key: 'free_subscription', label: 'Free Subscription', description: 'Bypass subscription requirement' },
      { key: 'budget_reminder', label: 'Budget Reminder', description: 'Show weekly budget label on dashboard Total Spent card (food filter only)' },
      { key: 'inventory_adjusted_cost_percent', label: 'dashboard.stockAdjusted.permission.label', description: 'dashboard.stockAdjusted.permission.description', translated: true },
      { key: 'file_batch_custom_ingredients', label: 'Price File Import', description: 'Upload supplier price files to batch-create custom ingredients (admin + staff)' },
      { key: 'file_batch_subrecipes', label: 'Subrecipe Template Import', description: 'Upload a template to batch-create subrecipes (admin + staff)' },
      { key: 'inventory_count_export', label: 'Inventory Count Export', description: 'Export button on the inventory count review page (admin-only)' },
    ],
  },
  {
    name: 'Automation Tools',
    settings: [
      { key: 'demo_account', label: 'Demo Account', description: 'Clear data buttons, edit quantities, excluded from stats' },
      { key: 'edit_quantity_info_override', label: 'Edit Quantity Info', description: 'Correct parsed quantity info from the order detail page' },
      { key: 'cost_price_automation', label: 'Cost Price Automation', description: 'Cost price automation tools' },
      { key: 'recipe_automation', label: 'Recipe Automation', description: 'Recipe automation tools' },
      { key: 'inventory_import_automation', label: 'Inventory Excel Import', description: 'Bootstrap inventory structure + counts from an Excel file' },
      { key: 'sourcing_improvement', label: 'Sourcing Improvement', description: 'Find cheaper alternatives across all restaurants' },
      { key: 'supplier_comparison', label: 'Supplier Comparison', description: 'Compare order-history products against an uploaded new-supplier price file' },
      { key: 'category_check', label: 'Check Categories', description: 'Second AI model audits stored product categories and proposes fixes (admin-only)' },
    ],
  },
];

const AdminAccountPermissionsPage = () => {
  const { t } = useTranslation();
  const [adminUsers, setAdminUsers] = useState<AdminUserWithStaff[]>([]);
  const [userSettings, setUserSettings] = useState<Map<string, Set<string>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('email') ?? '');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [userPricing, setUserPricing] = useState<Map<string, AdminPricing>>(new Map());
  const [pricingInputs, setPricingInputs] = useState<Map<string, { setupFee: string; pricePerRestaurant: string }>>(new Map());
  const [savingPricing, setSavingPricing] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [userStats, settingPairs] = await Promise.all([
        getAllAdminUsers(),
        getAllSettings(),
      ]);

      console.log('[Permissions] Admin users:', userStats.adminUsers);
      console.log('[Permissions] Setting pairs from API:', settingPairs);

      setAdminUsers(userStats.adminUsers);

      // Build a map of userId -> Set of setting keys
      const settingsMap = new Map<string, Set<string>>();
      settingPairs.forEach((pair: UserSettingPair) => {
        if (!settingsMap.has(pair.userId)) {
          settingsMap.set(pair.userId, new Set());
        }
        settingsMap.get(pair.userId)!.add(pair.settingKey);
      });
      console.log('[Permissions] Settings map:', Object.fromEntries(settingsMap));
      setUserSettings(settingsMap);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return adminUsers;
    const query = searchQuery.toLowerCase();
    return adminUsers.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        (user.name && user.name.toLowerCase().includes(query))
    );
  }, [adminUsers, searchQuery]);

  const handleToggleSetting = async (userId: string, settingKey: string) => {
    const currentSettings = userSettings.get(userId) || new Set();
    const hasThisSetting = currentSettings.has(settingKey);

    setUpdating(`${userId}-${settingKey}`);
    try {
      if (hasThisSetting) {
        await removeSetting(userId, settingKey);
        currentSettings.delete(settingKey);
      } else {
        await addSetting(userId, settingKey);
        currentSettings.add(settingKey);
      }

      // Update local state
      setUserSettings(new Map(userSettings.set(userId, new Set(currentSettings))));
    } catch (error) {
      console.error('Failed to update setting:', error);
    } finally {
      setUpdating(null);
    }
  };

  const loadPricingForUser = async (userId: string) => {
    if (userPricing.has(userId)) return;
    try {
      const pricing = await getAdminPricing(userId);
      setUserPricing(new Map(userPricing.set(userId, pricing)));
      setPricingInputs(new Map(pricingInputs.set(userId, {
        setupFee: (pricing.setup_fee / 100).toString(),
        pricePerRestaurant: (pricing.price_per_restaurant / 100).toString(),
      })));
    } catch (error) {
      console.error('Failed to load pricing:', error);
    }
  };

  const handleSavePricing = async (userId: string) => {
    const inputs = pricingInputs.get(userId);
    if (!inputs) return;

    const setupFeeCents = Math.round(parseFloat(inputs.setupFee || '0') * 100);
    const pricePerRestaurantCents = Math.round(parseFloat(inputs.pricePerRestaurant || '0') * 100);

    if (isNaN(setupFeeCents) || isNaN(pricePerRestaurantCents) || pricePerRestaurantCents <= 0) {
      alert('Please enter valid prices. Price per restaurant must be greater than 0.');
      return;
    }

    setSavingPricing(userId);
    try {
      await setAdminPricing(userId, {
        setup_fee: setupFeeCents,
        price_per_restaurant: pricePerRestaurantCents,
      });
      const updated = await getAdminPricing(userId);
      setUserPricing(new Map(userPricing.set(userId, updated)));
      setPricingInputs(new Map(pricingInputs.set(userId, {
        setupFee: (updated.setup_fee / 100).toString(),
        pricePerRestaurant: (updated.price_per_restaurant / 100).toString(),
      })));
    } catch (error) {
      console.error('Failed to save pricing:', error);
    } finally {
      setSavingPricing(null);
    }
  };

  const handleResetPricing = async (userId: string) => {
    setSavingPricing(userId);
    try {
      await resetAdminPricing(userId);
      const updated = await getAdminPricing(userId);
      setUserPricing(new Map(userPricing.set(userId, updated)));
      setPricingInputs(new Map(pricingInputs.set(userId, {
        setupFee: (updated.setup_fee / 100).toString(),
        pricePerRestaurant: (updated.price_per_restaurant / 100).toString(),
      })));
    } catch (error) {
      console.error('Failed to reset pricing:', error);
    } finally {
      setSavingPricing(null);
    }
  };

  const getUserSettingCount = (userId: string) => {
    return userSettings.get(userId)?.size ?? 0;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={cn(secondaryText)}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <h2 className={cn(primaryText, 'text-lg', semiBoldText, 'mb-2')}>
          Account Permissions
        </h2>
        <p className={cn(secondaryText, smallTextSize)}>
          Manage feature access for admin accounts. Settings are applied to the admin and may inherit to their staff.
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              'w-full pl-10 pr-4 py-2 rounded-lg border',
              'bg-white dark:bg-gray-800',
              'border-gray-200 dark:border-gray-700',
              'text-gray-900 dark:text-gray-100',
              'placeholder-gray-400 dark:placeholder-textQuaternary-dark',
              'focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-500 focus:border-transparent',
              smallTextSize
            )}
          />
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.length === 0 ? (
          <div className={cn(cardClass, 'p-8 text-center')}>
            <p className={secondaryText}>No users found</p>
          </div>
        ) : (
          filteredUsers.map((user) => {
            const isExpanded = expandedUserId === user.id;
            const settingCount = getUserSettingCount(user.id);

            return (
              <div key={user.id} className={cn(cardClass, 'overflow-hidden')}>
                {/* User Header */}
                <button
                  onClick={() => {
                    const newExpanded = isExpanded ? null : user.id;
                    setExpandedUserId(newExpanded);
                    if (newExpanded) loadPricingForUser(newExpanded);
                  }}
                  className={cn(
                    'w-full px-4 py-3 flex items-center justify-between',
                    'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                    'text-left'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        'bg-purple-100 dark:bg-purple-900/30',
                        'text-purple-600 dark:text-purple-400',
                        smallTextSize,
                        semiBoldText
                      )}
                    >
                      {(user.name || user.email)[0].toUpperCase()}
                    </div>
                    <div>
                      <div className={cn(primaryText, smallTextSize, semiBoldText)}>
                        {user.name || user.email}
                      </div>
                      <div className={cn(secondaryText, 'text-xs')}>
                        {user.email}
                      </div>
                      <div className={cn('text-xs text-gray-400 dark:text-gray-500 font-mono')}>
                        {user.id}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {settingCount > 0 && (
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded-full text-xs',
                          'bg-purple-100 dark:bg-purple-900/30',
                          'text-purple-600 dark:text-purple-400'
                        )}
                      >
                        {settingCount} {settingCount === 1 ? 'setting' : 'settings'}
                      </span>
                    )}
                    <svg
                      className={cn(
                        'w-4 h-4 text-gray-400 dark:text-textQuaternary-dark transition-transform',
                        isExpanded && 'rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>

                {/* Expanded Settings */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                    {SETTING_GROUPS.map((group) => (
                      <div key={group.name} className="mt-4">
                        <h4 className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-2')}>
                          {group.name}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {group.settings.map((setting) => {
                            const hasThisSetting = userSettings.get(user.id)?.has(setting.key) ?? false;
                            const isUpdating = updating === `${user.id}-${setting.key}`;

                            return (
                              <label
                                key={setting.key}
                                className={cn(
                                  'flex items-start gap-3 p-2 rounded-lg cursor-pointer',
                                  'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                                  isUpdating && 'opacity-50 pointer-events-none'
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={hasThisSetting}
                                  onChange={() => handleToggleSetting(user.id, setting.key)}
                                  disabled={isUpdating}
                                  className={cn(
                                    'mt-0.5 h-4 w-4 rounded',
                                    'border-gray-300 dark:border-gray-600',
                                    'text-purple-600 dark:text-purple-600 focus:ring-purple-500 dark:focus:ring-purple-500',
                                    'cursor-pointer'
                                  )}
                                />
                                <div>
                                  <div className={cn(primaryText, smallTextSize)}>
                                    {setting.translated ? t(setting.label) : setting.label}
                                  </div>
                                  <div className={cn(secondaryText, 'text-xs')}>
                                    {setting.translated ? t(setting.description) : setting.description}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {/* Payment Setup */}
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-3">
                        <h4 className={cn(secondaryText, 'text-xs uppercase tracking-wide')}>
                          Payment Setup
                        </h4>
                        {userPricing.get(user.id)?.isCustom ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                            Custom
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                            Default
                          </span>
                        )}
                      </div>

                      {pricingInputs.has(user.id) ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className={cn(primaryText, 'text-xs mb-1 block')}>
                                Setup Fee (€, one-time)
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={pricingInputs.get(user.id)?.setupFee ?? ''}
                                onChange={(e) => {
                                  const current = pricingInputs.get(user.id)!;
                                  setPricingInputs(new Map(pricingInputs.set(user.id, {
                                    ...current,
                                    setupFee: e.target.value,
                                  })));
                                }}
                                className={cn(
                                  'w-full px-3 py-2 rounded-lg border',
                                  'bg-white dark:bg-gray-800',
                                  'border-gray-200 dark:border-gray-700',
                                  'text-gray-900 dark:text-gray-100',
                                  'focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-500 focus:border-transparent',
                                  smallTextSize
                                )}
                              />
                            </div>
                            <div>
                              <label className={cn(primaryText, 'text-xs mb-1 block')}>
                                Price per Restaurant (€/month)
                              </label>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={pricingInputs.get(user.id)?.pricePerRestaurant ?? ''}
                                onChange={(e) => {
                                  const current = pricingInputs.get(user.id)!;
                                  setPricingInputs(new Map(pricingInputs.set(user.id, {
                                    ...current,
                                    pricePerRestaurant: e.target.value,
                                  })));
                                }}
                                className={cn(
                                  'w-full px-3 py-2 rounded-lg border',
                                  'bg-white dark:bg-gray-800',
                                  'border-gray-200 dark:border-gray-700',
                                  'text-gray-900 dark:text-gray-100',
                                  'focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-500 focus:border-transparent',
                                  smallTextSize
                                )}
                              />
                            </div>
                          </div>
                          <div className={cn(secondaryText, 'text-xs')}>
                            Yearly price auto-calculated: €{((parseFloat(pricingInputs.get(user.id)?.pricePerRestaurant || '0') * 10) || 0).toLocaleString('en-US')}/restaurant/year (10 months, ~16% discount)
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSavePricing(user.id)}
                              disabled={savingPricing === user.id}
                              className={cn(
                                'px-4 py-1.5 rounded-lg text-xs font-medium',
                                'bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                                'transition-colors'
                              )}
                            >
                              {savingPricing === user.id ? 'Saving...' : 'Save Pricing'}
                            </button>
                            {userPricing.get(user.id)?.isCustom && (
                              <button
                                onClick={() => handleResetPricing(user.id)}
                                disabled={savingPricing === user.id}
                                className={cn(
                                  'px-4 py-1.5 rounded-lg text-xs font-medium',
                                  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200',
                                  'hover:bg-gray-200 dark:hover:bg-gray-600',
                                  'disabled:opacity-50 disabled:cursor-not-allowed',
                                  'transition-colors'
                                )}
                              >
                                Reset to Default
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={cn(secondaryText, 'text-xs')}>Loading pricing...</div>
                      )}
                    </div>

                    {/* Staff info */}
                    {user.staff.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <div className={cn(secondaryText, 'text-xs')}>
                          <span className="font-medium">{user.staff.length} staff member{user.staff.length !== 1 ? 's' : ''}</span>
                          {' '}will inherit applicable settings (Invoices, Food-Only Dashboard)
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className={cn(cardClass, 'p-4 mt-6')}>
        <h4 className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-2')}>
          Inheritance Rules
        </h4>
        <div className={cn(secondaryText, 'text-xs space-y-1')}>
          <p><span className="font-medium">Admin-only:</span> MEP, Checklists, Recipes, Notifications, Demo Account, Cost Price Automation, Recipe Automation, Free Subscription</p>
          <p><span className="font-medium">Staff inherits:</span> Invoices, Food-Only Dashboard</p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAccountPermissionsPage;
